import { GetTokenSilentlyOptions } from '@auth0/auth0-react';
import {
    ApiSourceFormData,
    ApiResponse,
    ApiResponseData
} from '../types/api';
import { API_ENDPOINTS } from "../config/api.config";

type HeadersRecord = Record<string, string>;

export class ApiError extends Error {
    constructor(
        message: string,
        public status?: number,
        public code?: string
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

export class ApiService {
    private static getTokenFn: ((options?: GetTokenSilentlyOptions) => Promise<string>) | null = null;

    public static setTokenGetter(getTokenFn: (options?: GetTokenSilentlyOptions) => Promise<string>) {
        ApiService.getTokenFn = getTokenFn;
    }

    private async handleTokenError(error: Error) {
        if (error.message.includes('login_required') ||
            error.message.includes('invalid_grant')) {
            this.handleAuthError();
        }
        throw error;
    }

    private async getToken(): Promise<string> {
        if (!ApiService.getTokenFn) {
            throw new ApiError('Token getter not set');
        }

        try {
            return await ApiService.getTokenFn();
        } catch (error) {
            console.error('Error getting token:', error);
            await this.handleTokenError(error as Error);
            throw new ApiError('Failed to get authentication token');
        }
    }

    private isPublicEndpoint(url: string): boolean {
        try {
            const publicPatterns = [
                '/api/auth/login',
                '/api/auth/signup',
                '/api/auth/check-login-options',
                '/api/users/accept-invitation',
                '/api/auth/check-org-settings',
                '/api/auth/verify-auth0-token',
                '/api/auth/auth0/verify',
                '/api/auth/refresh-token'
            ];

            const urlObj = new URL(url, window.location.origin);
            const urlPath = urlObj.pathname;
            return publicPatterns.some(pattern =>
                urlPath === pattern ||
                urlPath === pattern.replace('/api/', '/') ||
                urlPath === `/api${pattern}`
            );
        } catch (error) {
            console.error('Invalid URL:', url);
            return false;
        }
    }

    private createHeaders(init?: HeadersInit): Headers {
        const headers = new Headers(init);
        if (!headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
        }
        if (!headers.has('Accept')) {
            headers.set('Accept', 'application/json');
        }
        return headers;
    }

    private async getAuthHeaders(): Promise<Headers> {
        const headers = this.createHeaders();
        const token = await this.getToken();
        headers.set('Authorization', `Bearer ${token}`);
        return headers;
    }

    private handleAuthError() {
        localStorage.removeItem('auth0_access_token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
    }

    public async verifyAuth0Token(token: string, email: string) {
        if (!token || !email) {
            throw new Error('Token and email are required');
        }

        console.log('Verifying Auth0 token...', {
            tokenLength: token.length,
            emailPresent: !!email
        });

        const response = await fetch(API_ENDPOINTS.AUTH.AUTH0.VERIFY_TOKEN, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ email }),
            credentials: 'include'
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            console.error('Token verification failed:', {
                status: response.status,
                error
            });
            throw new Error(error.message || 'Token verification failed');
        }

        const data = await response.json();
        if (!data.token || !data.user) {
            throw new Error('Invalid response format');
        }

        return data;
    }

    private async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
        const finalUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;

        try {
            const headers = this.isPublicEndpoint(finalUrl)
                ? this.createHeaders(options.headers)
                : await this.getAuthHeaders();

            Object.entries(options.headers || {}).forEach(([key, value]) => {
                if (value) headers.set(key, value);
            });

            const response = await fetch(finalUrl, {
                ...options,
                headers,
                credentials: 'include'
            });

            if (response.status === 401) {
                try {
                    // Try to get a new token before giving up
                    await this.getToken();
                    // If successful, retry the request once
                    return await this.fetchWithAuth(url, options);
                } catch (tokenError) {
                    await this.handleTokenError(tokenError as Error);
                    throw new ApiError('Session expired', 401);
                }
            }

            return response;
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }

            if (error instanceof TypeError && error.message === 'Failed to fetch') {
                throw new ApiError('Network error or CORS issue. Please check your connection.');
            }

            console.error('Fetch error:', error);
            throw new ApiError('Request failed');
        }
    }

    public async get<T>(url: string, options: RequestInit = {}): Promise<T> {
        const response = await this.fetchWithAuth(url, {
            ...options,
            method: 'GET'
        });
        return this.processResponse<T>(response);
    }

    public async post<T>(url: string, data: unknown, options: RequestInit = {}): Promise<T> {
        const response = await this.fetchWithAuth(url, {
            ...options,
            method: 'POST',
            body: JSON.stringify(data)
        });
        return this.processResponse<T>(response);
    }

    public async put<T>(url: string, data: unknown, options: RequestInit = {}): Promise<T> {
        const response = await this.fetchWithAuth(url, {
            ...options,
            method: 'PUT',
            body: JSON.stringify(data)
        });
        return this.processResponse<T>(response);
    }

    public async delete<T>(url: string, options: RequestInit = {}): Promise<T> {
        const response = await this.fetchWithAuth(url, {
            ...options,
            method: 'DELETE'
        });
        return this.processResponse<T>(response);
    }

    public async getPublic<T>(url: string, options: RequestInit = {}): Promise<T> {
        const response = await fetch(url, {
            ...options,
            method: 'GET',
            headers: this.createHeaders(options.headers)
        });
        return this.processResponse<T>(response);
    }

    public async postPublic<T>(url: string, data: unknown, options: RequestInit = {}): Promise<T> {
        const response = await fetch(url, {
            ...options,
            method: 'POST',
            headers: this.createHeaders({
                'Content-Type': 'application/json',
                ...options.headers
            }),
            body: JSON.stringify(data)
        });
        return this.processResponse<T>(response);
    }

    public async patch<T>(url: string, data: unknown, options: RequestInit = {}): Promise<T> {
        const response = await this.fetchWithAuth(url, {
            ...options,
            method: 'PATCH',
            body: JSON.stringify(data)
        });
        return this.processResponse<T>(response);
    }

    private async processResponse<T>(response: Response): Promise<T> {
        if (!response.ok) {
            let errorMessage = response.statusText;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
                console.error('API Error:', {
                    status: response.status,
                    message: errorMessage,
                    data: errorData,
                    url: response.url
                });
                throw new ApiError(errorMessage, response.status);
            } catch (parseError) {
                if (parseError instanceof ApiError) {
                    throw parseError;
                }
                console.error('Error parsing error response:', parseError);
                throw new ApiError(errorMessage, response.status);
            }
        }

        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
            try {
                return await response.json();
            } catch (parseError) {
                console.error('Error parsing JSON response:', parseError);
                throw new ApiError('Failed to parse JSON response');
            }
        }
        return response.text() as unknown as T;
    }

    public async testApiEndpoint(config: ApiSourceFormData & { timeout_ms?: number }): Promise<ApiResponse> {
        const {
            endpoint,
            method = 'GET',
            headers = {},
            body,
            timeout_ms = 30000
        } = config;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout_ms);

        try {
            const requestHeaders = await this.getAuthHeaders();
            Object.entries(headers).forEach(([key, value]) => {
                if (value) {
                    requestHeaders.set(key, value);
                }
            });

            const requestInit: RequestInit = {
                method,
                headers: requestHeaders,
                signal: controller.signal,
                credentials: 'include'
            };

            if (method !== 'GET' && body) {
                requestInit.body = typeof body === 'string' ? body : JSON.stringify(body);
            }

            const response = await fetch(endpoint, requestInit);
            const responseHeaders: HeadersRecord = {};
            response.headers.forEach((value, key) => {
                responseHeaders[key] = value;
            });

            let data: ApiResponseData;
            const contentType = response.headers.get('content-type');
            if (contentType?.includes('application/json')) {
                data = await response.json();
            } else {
                const textData = await response.text();
                data = { value: textData };
            }

            return {
                data,
                headers: responseHeaders,
                status: response.status
            };
        } catch (error) {
            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new ApiError(`Request timed out after ${timeout_ms}ms`, 408);
                }
                throw new ApiError(error.message);
            }
            throw new ApiError('Unknown error occurred');
        } finally {
            clearTimeout(timeoutId);
        }
    }

    public async uploadFile<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
        const formData = new FormData();
        formData.append('file', file);

        try {
            const token = await this.getToken();

            return new Promise<T>((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                if (onProgress) {
                    xhr.upload.onprogress = (event) => {
                        if (event.lengthComputable) {
                            const progress = (event.loaded / event.total) * 100;
                            onProgress(progress);
                        }
                    };
                }

                xhr.open('POST', url, true);
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);

                xhr.onload = async () => {
                    if (xhr.status === 401) {
                        try {
                            // Try to get a new token
                            await this.getToken();
                            // If successful, retry the upload
                            const result = await this.uploadFile<T>(url, file, onProgress);
                            resolve(result);
                            return;
                        } catch (tokenError) {
                            await this.handleTokenError(tokenError as Error);
                            reject(new ApiError('Session expired', 401));
                            return;
                        }
                    }

                    if (xhr.status >= 200 && xhr.status < 300) {
                        try {
                            const response = JSON.parse(xhr.responseText) as T;
                            resolve(response);
                        } catch (parseError) {
                            reject(new ApiError('Invalid JSON response'));
                        }
                    } else {
                        reject(new ApiError('Upload failed', xhr.status));
                    }
                };

                xhr.onerror = () => reject(new ApiError('Network error'));
                xhr.send(formData);
            });
        } catch (error) {
            await this.handleTokenError(error as Error);
            throw error instanceof ApiError ? error : new ApiError('Authentication required');
        }
    }

    public async downloadFile(url: string, filename: string): Promise<void> {
        const response = await this.fetchWithAuth(url, {
            method: 'GET',
        });

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    }
}

export const apiService = new ApiService();

export const testApiEndpoint = (config: ApiSourceFormData & { timeout_ms?: number }): Promise<ApiResponse> =>
    apiService.testApiEndpoint(config);